import { Box } from '@factorialco/deprecated-design-system'
import { SectionHeader } from '@factorialco/f0-react/dist/experimental'
import { Features } from 'generated/featureDefinitions'
import { ContributorsListPageDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import ContributorsDataCollection from 'modules/project_management/components/tables/ContributorsDataCollection'
import { UpsellContributorsEmptyState } from 'modules/project_management/components/upsells/UpsellContributorsEmptyState'
import { ProjectsUpsellRequestSources } from 'modules/project_management/components/upsells/constants'

const ContributorsListPageContent = ({
  isTotalProfitabilityEnabled,
  canManageRates,
  canSeeRates,
}: {
  isTotalProfitabilityEnabled: boolean
  canManageRates: boolean
  canSeeRates: boolean
}) => {
  return (
    <>
      {isTotalProfitabilityEnabled && (
        <SectionHeader
          title={i18n.t('project_management.people_and_rates.people_header.title')}
          description={i18n.t('project_management.people_and_rates.people_header.description')}
          link={{
            href: `https://help.factorialhr.com/${i18n.t(
              'project_management.projects.lower_case_title'
            )}`,
            label: i18n.t('project_management.people_and_rates.people_header.secondary_button'),
          }}
        />
      )}

      <ContributorsDataCollection
        isTotalProfitabilityEnabled={isTotalProfitabilityEnabled}
        canManageRates={canManageRates}
        canSeeRates={canSeeRates}
      />
    </>
  )
}

const ContributorsListPage = () => {
  useEffect(() => {
    track(trackingEvents.projectManagement.contributors.visitContributorsListSection)
  }, [])

  const {
    company: { id: companyId },
  } = useCurrentSession()

  const {
    data: page,
    loading,
    error,
  } = useQuery(ContributorsListPageDocument, {
    variables: {
      companyId: companyId,
      featureIds: [
        Features.projects_contributors,
        Features.projects_budget,
        Features.projects_costs,
      ],
    },
  })

  const hasContributorsFeature = !!page?.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.projects_contributors
  )?.available

  const isBudgetFeatureEnabled = !!page?.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.projects_budget
  )?.available

  const isCostsFeatureEnabled = !!page?.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.projects_costs
  )?.available

  const isTotalProfitabilityEnabled = !!page?.features.featuresConnection.nodes.find(
    (feature) => feature.name === 'dev_projects_total_profitability'
  )?.enabled

  const canManageRates = !!page?.projectManagement._policies.rateManage.can
  const canSeeLaborCost = !!page?.projectManagement._policies.projectSeeLaborCost.can
  const canSeeRates = isBudgetFeatureEnabled && isCostsFeatureEnabled && canSeeLaborCost
  const hasAnyProject = (page?.projectManagement.projectsConnection.totalCount ?? 0) > 0
  const canSeeContributors = hasContributorsFeature && hasAnyProject
  const canSeePage = !hasContributorsFeature || canSeeContributors

  return (
    <ResponsivePage.Body variant="wide" loading={loading} error={error} authorized={canSeePage}>
      {!hasContributorsFeature ? (
        <Box alignItems="center" justifyContent="center" style={{ height: '50vh' }}>
          <UpsellContributorsEmptyState
            requestSource={ProjectsUpsellRequestSources.contributors}
            description={i18n.t('project_management.upselling.people')}
          />
        </Box>
      ) : (
        <ContributorsListPageContent
          isTotalProfitabilityEnabled={isTotalProfitabilityEnabled}
          canManageRates={canManageRates}
          canSeeRates={canSeeRates}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default ContributorsListPage
